import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyFacturePlanPage() {
  const { qrToken } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    fetch(`${API_URL}/api/factures-plans/verify/public/${qrToken}`)
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => setState({ loading: false, data: ok && j.valid ? j : null, error: j?.message || (!j.valid ? 'Document non vérifié' : null) }))
      .catch(() => setState({ loading: false, data: null, error: 'Erreur réseau' }));
  }, [qrToken]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Vérification en cours…</div>
      </div>
    );
  }

  const valid = !!state.data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 flex items-center justify-center p-6">
      <Card className="max-w-2xl w-full border-2 shadow-2xl" data-testid="verify-facture-card">
        <CardContent className="p-8">
          <div className="flex flex-col items-center mb-6">
            {valid ? (
              <CheckCircle className="w-20 h-20 text-emerald-600" />
            ) : (
              <XCircle className="w-20 h-20 text-red-600" />
            )}
            <h1 className={`text-2xl font-bold mt-4 ${valid ? 'text-emerald-700' : 'text-red-700'}`}>
              {valid ? 'Document authentique' : 'Document non vérifié'}
            </h1>
            {!valid && (
              <p className="text-sm text-slate-500 mt-2">{state.error || 'Ce document n\'est pas reconnu dans nos registres.'}</p>
            )}
          </div>

          {valid && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-700" />
                  <span className="font-mono font-bold text-lg text-blue-800">{state.data.reference}</span>
                </div>
                <Badge className={state.data.type_document === 'recu' ? 'bg-emerald-700 text-white' : 'bg-blue-700 text-white'}>
                  {state.data.type_document === 'recu' ? 'REÇU' : 'FACTURE'}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Destinataire</div>
                  <div className="font-medium">{state.data.raison_sociale || state.data.destinataire}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Plan</div>
                  <div className="font-medium">{state.data.plan_nom}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Montant TTC</div>
                  <div className="font-bold text-lg text-blue-800">{Number(state.data.montant_ttc || 0).toFixed(2)} €</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Émis le</div>
                  <div className="font-medium">{state.data.emission_date}</div>
                </div>
                {state.data.paid_at && (
                  <div className="col-span-2">
                    <div className="text-xs uppercase tracking-wider text-slate-500">Réglé le</div>
                    <div className="font-medium text-emerald-700">{state.data.paid_at}</div>
                  </div>
                )}
              </div>

              <div className="text-xs text-slate-500 text-center pt-4 border-t mt-4">
                Document émis par <strong>OBJECTIF SCOP OUTREMER</strong> — OSO O'SCOP Outremer
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
